import { DEFAULT_WHATS_NEW_MAX_DAYS, WhatsNewCardI } from "./types";

/**
 * When a card shows (C16-D311, C16-D312). A card is live from `startsAt` up
 * to `endsAt`, and shows at most once a day: the day's first signed-in visit
 * is the "login" Omar asked to announce at. Dismissing it records the local
 * day, so it comes back the next day while it is still live.
 */

/** Card id → the local day ("YYYY-MM-DD") it was last dismissed. */
export type WhatsNewSeenI = Record<string, string>;

const pad = (value: number) => String(value).padStart(2, "0");

/** The viewer's local calendar day, "YYYY-MM-DD". */
export const getLocalDayKey = (now: Date): string =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

const toTime = (iso: string) => new Date(iso).getTime();

/** Live at `now`; a card with an unreadable date is never live. */
export const isWhatsNewLive = (card: WhatsNewCardI, now: Date): boolean => {
  const startsAt = toTime(card.startsAt);
  const endsAt = toTime(card.endsAt);

  if (Number.isNaN(startsAt) || Number.isNaN(endsAt)) {
    return false;
  }

  return startsAt <= now.getTime() && now.getTime() < endsAt;
};

/** Card id → the ET days ("YYYY-MM-DD") it was shown on (C16-D425). */
export type WhatsNewShownDaysI = Record<string, Array<string>>;

const ET_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The ET calendar day of `now`, "YYYY-MM-DD". */
export const getEtDayKey = (now: Date): string => ET_DAY.format(now);

/**
 * Under the card's day cap: shown on fewer ET days than its maximum, or
 * already shown today (a second visit the same day still counts as one).
 */
export const isUnderWhatsNewDayCap = (
  card: WhatsNewCardI,
  shownDays: WhatsNewShownDaysI,
  now: Date
): boolean => {
  const days = shownDays[card.id] ?? [];

  return (
    days.includes(getEtDayKey(now)) ||
    days.length < (card.maxDaysPerUser ?? DEFAULT_WHATS_NEW_MAX_DAYS)
  );
};

/**
 * The cards to show now, newest first: live, not yet dismissed today, and
 * under their day cap.
 */
export const getWhatsNewCardsToShow = (
  cards: Array<WhatsNewCardI>,
  seen: WhatsNewSeenI,
  now: Date,
  shownDays: WhatsNewShownDaysI = {}
): Array<WhatsNewCardI> => {
  const today = getLocalDayKey(now);

  return cards
    .filter(
      (card) =>
        isWhatsNewLive(card, now) &&
        seen[card.id] !== today &&
        isUnderWhatsNewDayCap(card, shownDays, now)
    )
    .sort((a, b) => toTime(b.startsAt) - toTime(a.startsAt));
};

/**
 * Records today (ET) for the cards shown now. Only cards still live are
 * kept, so the record never outgrows the cards on air.
 */
export const markWhatsNewShown = (
  shownDays: WhatsNewShownDaysI,
  cardIds: Array<string>,
  liveCardIds: Array<string>,
  now: Date
): WhatsNewShownDaysI => {
  const today = getEtDayKey(now);
  const next: WhatsNewShownDaysI = {};

  liveCardIds.forEach((id) => {
    if (shownDays[id]) {
      next[id] = shownDays[id];
    }
  });

  cardIds.forEach((id) => {
    const days = next[id] ?? shownDays[id] ?? [];

    next[id] = days.includes(today) ? days : [...days, today];
  });

  return next;
};

/**
 * Records `cardIds` as dismissed today. Only cards still live are kept, so
 * the record never outgrows the cards on air.
 */
export const markWhatsNewSeen = (
  seen: WhatsNewSeenI,
  cardIds: Array<string>,
  liveCardIds: Array<string>,
  now: Date
): WhatsNewSeenI => {
  const today = getLocalDayKey(now);
  const next: WhatsNewSeenI = {};

  liveCardIds.forEach((id) => {
    if (seen[id]) {
      next[id] = seen[id];
    }
  });

  cardIds.forEach((id) => {
    next[id] = today;
  });

  return next;
};
